import { useEffect, useMemo, useRef } from "react";
import { releaseHtmlThemeOutsideOwners } from "@/lib/html-theme-owner";
import { isEdgeCacheableRequest } from "../workers/edge-cache";
import { markResponseDegraded } from "@/lib/edge-degraded.server";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useRouteError,
  useLoaderData,
  useRouteLoaderData,
  useLocation,
  useMatches,
  data,
} from "react-router";
import { isLocaleCode, LOCALES } from "./lib/i18n/locales";
import type {
  LoaderFunctionArgs,
  AppLoadContext,
  MetaFunction,
  ShouldRevalidateFunctionArgs,
} from "react-router";
import {
  NotFoundScreen,
  ServerErrorScreen,
} from "./components/errors/ErrorScreens";
import {
  errorRefFromRequest,
  markDocumentHydrated,
  newErrorRef,
  pickErrorRef,
} from "./lib/error-ref";
import { I18nProvider, useI18n } from "./contexts/i18n.context";
import { PostHogProvider } from "./components/analytics/PostHogProvider";
import { isAnalyticsMutedEmbed } from "./lib/analytics/embed-mute";
import { IS_STAGING_BUILD } from "./lib/staging-flag";
import { isDemoPath } from "./lib/demo/demo-view";

// Client-side ENV type declaration
declare global {
  interface Window {
    ENV: {
      POSTHOG_API_KEY?: string;
      POSTHOG_API_HOST?: string;
      DECK_BUILDER_ENABLED?: string;
      GIT_TOOL_ENABLED?: string;
      HTTP_TOOL_ENABLED?: string;
      ALGORITHM_TOOL_ENABLED?: string;
      SECURITY_TOOL_ENABLED?: string;
    };
  }
}
import { ToastProvider } from "./components/common/ToastProvider";
import { HeaderProvider } from "./contexts/header.context";
import { BreadcrumbProvider } from "./components/common/Breadcrumb/BreadcrumbProvider";
import { AuthProvider, useAuth } from "./contexts/auth.context";
import { AnalyticsIdentitySync } from "./components/analytics/AnalyticsIdentitySync";
import { ReferralLandingSync } from "./components/referral/ReferralLandingSync";
import { ReferralShareProvider } from "./components/referral/ReferralShareProvider";
import { MotionUIThemeProvider } from "./components/motion-ui/ui-theme";
import { LessonGlossaryTermsProvider } from "@noiman/ui/lesson-glossary";
import { renderLessonGlossaryTerms } from "./components/glossary/LessonGlossaryTermCards";
import { LessonCurtainProvider } from "./components/lessons/LessonCurtain";
import motionTheme from "./motion.theme";
import {
  TeamProvider,
  type Team,
  type PlanInfo,
} from "./contexts/team.context";
import { flushAuthCookies } from "@noiman/shared-auth/auth-fetch";
import {
  getRequestAuthBootstrapStatus,
  type AuthBootstrapStatus,
} from "@noiman/shared-auth/auth-bootstrap";
import { primeAuthClientBootstrap } from "./lib/auth-client-bootstrap";
import {
  captureSentryException,
  setSentryUser,
} from "./lib/sentry.client";
import { GlobalSearch } from "./components/common/GlobalSearch/GlobalSearch";
import {
  GlobalSearchProvider,
  useGlobalSearch,
} from "./contexts/global-search.context";
import { useCmdK } from "./hooks/useKeyboardShortcut";
import {
  getLuagateBootstrapFromRequest,
  getOptionalLuagateAccess,
  getMyTeamsFromRequest,
} from "./lib/team.server";
import { shouldSkipRootRevalidationForLessonFetcher } from "./pages/courses/lessons/lesson-fetcher-revalidation";
import { isDeckBuilderEnabledForContext } from "./lib/deck-builder/feature-flag";
import { generateRootFallbackMeta } from "./lib/seo-config";
import { isGitToolEnabledForContext } from "./lib/tools/git/feature-flag";
import { isHttpToolEnabledForContext } from "./lib/tools/http/feature-flag";
import { isAlgorithmToolEnabledForContext } from "./lib/tools/algorithm/feature-flag";
import { isSecurityToolEnabledForContext } from "./lib/tools/security/feature-flag";
import { buildFooterSections } from "./lib/footer-sections.server";
import {
  PageFooterSectionsProvider,
  type PageFooterSection,
} from "@noiman/ui/page-footer";
import "./app.css";
import "@noiman/ui/styles";

const slideQaLocalOnly = import.meta.env.VITE_SLIDE_QA_LOCAL_ONLY === "1";

/*
本文の字体 Noto Sans JP の CSS。regular と bold だけを読み、中間の太さはブラウザが寄せる。

Zen Kaku Gothic New の 900 は PageHead の h1 (page-parts.css) と schools.css の節の見出しの字体。--ui-font-display は .lg の中でしか決まらないので、公開ページでは fallback の先頭が使われる (10/24 代表「明朝が気になる、Zen Kaku でよくないか」で明朝から替え、同じ日に /security と同じ 900 へ揃えた)。
Noto Serif JP はここではもう読まない (10/24 使っている字の棚卸しで外した)。公開の頁 (PageHead、schools.css、/lab の .lab-mincho、/community) は Zen Kaku に替わって使わなくなり、/luagate (components/luagate-ui/LgRoot.tsx の NOTO_SERIF_JP) と cloudeditor の BootScreen (名言の明朝、600) は自分で別に読んでいるので、ここの 700 は重複だった。OG 画像の生成 (lib/seo/*-og.ts) はサーバー側で loadGoogleFontForText が別に読むので、この <link> とは関係ない

最初の表示を止めないために media="print" で読み始め、届いたら all に切り替える。
止めていた頃は、スマホの Lighthouse がこの1本だけで約1.4秒の遅れを見積もっていた (2026-10-08 本番)。
字が届くまでは tailwind.config.ts の後ろの並び (ヒラギノ、游ゴシック、端末の sans-serif) で
描き、display=swap で差し替わる。差し替わり自体は前から起きていた。IBM Plex Mono を足した分、図の中のコードの字の幅も差し替わりで変わる (図の外枠は固定)

IBM Plex Mono の 700 は、本文に埋めた手描きの図の中のコード (class tc) の字体。
Google Fonts は字の範囲ごとに分けて配るので、図の無いページでは字体の本体は取りに行かない
*/
const NOTO_SANS_JP_STYLESHEET =
  "https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;700&family=Zen+Kaku+Gothic+New:wght@900&family=IBM+Plex+Mono:wght@700&display=swap";
const NOTO_SANS_JP_STYLESHEET_ID = "noto-sans-jp-stylesheet";
/*
ハイドレーションより前に走らせる。JS の束を待つと、字の切り替えが数秒遅れる。
読み込み済みなら sheet が埋まっているので、その場で切り替える
*/
const NOTO_SANS_JP_ACTIVATE_SCRIPT = `(function(l){if(!l)return;function on(){l.media='all'}if(l.sheet){on()}else{l.addEventListener('load',on,{once:true})}})(document.getElementById('${NOTO_SANS_JP_STYLESHEET_ID}'))`;

interface CloudflareEnv {
  POSTHOG_API_KEY?: string;
  POSTHOG_API_HOST?: string;
  DECK_BUILDER_ENABLED?: string;
  GIT_TOOL_ENABLED?: string;
  HTTP_TOOL_ENABLED?: string;
  ALGORITHM_TOOL_ENABLED?: string;
  SECURITY_TOOL_ENABLED?: string;
}

interface RootLoaderData {
  /** 500 の画面に出す問い合わせ番号。handleError のログと同じ値 */
  errorRef: string | null;
  teams: Team[];
  planInfo: PlanInfo | null;
  /** 公開の頁の帯に「ダッシュボード」の入口を出してよい人か。判定は LuaGate の入口と同じ getOptionalLuagateAccess */
  luagateEntry: boolean;
  /** LuaGate の所属の役割。入場判定が通らない人 (所属が無い、取り消し、体験の期限切れ) は null */
  luagateRole: string | null;
  /** 所属を読めたか。loader が失敗して teams を空で返したときは false (受講生かどうか分からない) */
  teamsKnown: boolean;
  authBootstrap: AuthBootstrapStatus;
  /** フッタ下段の区画 (可視化、言語、Git コマンド)。中身は lib/footer-sections.server.ts */
  footerSections: PageFooterSection[];
  ENV: {
    POSTHOG_API_KEY?: string;
    POSTHOG_API_HOST?: string;
    DECK_BUILDER_ENABLED?: string;
    GIT_TOOL_ENABLED?: string;
    HTTP_TOOL_ENABLED?: string;
    ALGORITHM_TOOL_ENABLED?: string;
    SECURITY_TOOL_ENABLED?: string;
  };
}

export async function loader({ request, context }: LoaderFunctionArgs) {
  // Get Cloudflare environment variables
  const env = (context as any)?.cloudflare?.env as CloudflareEnv | undefined;
  const authBootstrap = getRequestAuthBootstrapStatus(request);
  // 縁に5分置かれる要求の HTML に番号を焼くと、HIT した全員が同じ番号を持つ。
  // 焼かず、必要になった側(ブラウザのエラー画面)で振る
  const errorRef = isEdgeCacheableRequest(request)
    ? null
    : (errorRefFromRequest(request) ?? newErrorRef());

  try {
    const pathname = new URL(request.url).pathname;
    const isLuagateRequest = pathname.startsWith("/luagate");
    // スキルマップの見本 (/women が iframe で埋め込む) は作り物の進みしか描かないので、
    // ログインしていても本人の所属と契約を読まず、HTML に載せない
    // ログイン無しのデモ (/demo) も同じ。固定の見本しか描かないので、ログインしている人が開いても api-go を読まない
    const isSkillMapDemo =
      pathname === "/skillmap/demo" || isDemoPath(pathname);
    const [teams, planInfo] =
      authBootstrap === "anonymous" || isSkillMapDemo
        ? ([[], null] as const)
        : isLuagateRequest
          ? await getLuagateBootstrapFromRequest(request, context).then(
              (bootstrap) =>
                [
                  // 受講の権利の取り消しの印がある人には LuaGate をメニューに出さない
                  bootstrap.team &&
                  bootstrap.member &&
                  bootstrap.member.accessRevoked !== true
                    ? [bootstrap.team]
                    : [],
                  // LuaGate bootstrap intentionally excludes plan info. Its
                  // access gate and pages use membership fields only; pricing
                  // gets MyPlanInfo through the non-LuaGate path below.
                  null,
                ] as const,
            )
          : await import("@noiman/shared-graphql").then(
              async ({ getMyPlanInfo }) =>
                Promise.all([
                  getMyTeamsFromRequest(request, context),
                  getMyPlanInfo({ request }),
                ]),
            );
    // 帯の入口は、受講の権利の取り消しと体験の期限切れを除く LuaGate の入場判定に合わせる。
    // SSR の時点で決めるので、ログインの確定を待たずに帯が同じ形で出る。
    // 判定は API を読むので、公開の帯を出す頁で、所属に luagate がある人のときだけ走らせる
    const luagateAccess =
      authBootstrap === "anonymous" ||
      isSkillMapDemo ||
      isLuagateRequest ||
      !teams.some((t) => t.slug === "luagate")
        ? null
        : await getOptionalLuagateAccess(request, context);
    const luagateEntry = luagateAccess !== null;
    const result: RootLoaderData = {
      errorRef,
      authBootstrap,
      footerSections: buildFooterSections(),
      luagateEntry,
      luagateRole: luagateAccess?.memberRole ?? null,
      teamsKnown: true,
      teams: teams.map((t) => ({
        id: t.id,
        name: t.name,
        slug: t.slug,
        iconUrl: t.iconUrl ?? null,
      })),
      planInfo: planInfo
        ? {
            planType: planInfo.planType,
            planName: planInfo.planName,
            isActive: planInfo.isActive,
          }
        : null,
      ENV: {
        POSTHOG_API_KEY: env?.POSTHOG_API_KEY,
        POSTHOG_API_HOST: env?.POSTHOG_API_HOST,
        // Deck Builder の出し分け。索引と柱がクライアント側で絞るので
        // loader 経由で値を届ける (window.ENV にも同じ物が乗る)。
        // ページ側の判定 (isDeckBuilderEnabledForContext) と同じ場所を
        // 見る — cloudflare の env が無いローカルでは process.env を
        // 見るので、直の住所は開けるのに索引に出ないずれを防ぐ
        DECK_BUILDER_ENABLED: isDeckBuilderEnabledForContext(context)
          ? "true"
          : undefined,
        // Git Game の出し分けも同じ形で
        GIT_TOOL_ENABLED: isGitToolEnabledForContext(context)
          ? "true"
          : undefined,
        // HTTP Game も同じ
        HTTP_TOOL_ENABLED: isHttpToolEnabledForContext(context)
          ? "true"
          : undefined,
        // Algorithm Game も同じ形で
        ALGORITHM_TOOL_ENABLED: isAlgorithmToolEnabledForContext(context)
          ? "true"
          : undefined,
        // Security Bug Hunt も同じ形で
        SECURITY_TOOL_ENABLED: isSecurityToolEnabledForContext(context)
          ? "true"
          : undefined,
      },
    };
    const response = flushAuthCookies(request, new Response(null));
    return data(result, { headers: response.headers });
  } catch (error) {
    // Not logged in or error - return empty teams.
    // 失敗の代わりの値で描いた HTML は、縁に置かない
    markResponseDegraded();
    const result: RootLoaderData = {
      errorRef,
      authBootstrap,
      footerSections: buildFooterSections(),
      luagateEntry: false,
      luagateRole: null,
      teamsKnown: false,
      teams: [],
      planInfo: null,
      ENV: {
        POSTHOG_API_KEY: env?.POSTHOG_API_KEY,
        POSTHOG_API_HOST: env?.POSTHOG_API_HOST,
        DECK_BUILDER_ENABLED: isDeckBuilderEnabledForContext(context)
          ? "true"
          : undefined,
        GIT_TOOL_ENABLED: isGitToolEnabledForContext(context)
          ? "true"
          : undefined,
        HTTP_TOOL_ENABLED: isHttpToolEnabledForContext(context)
          ? "true"
          : undefined,
        ALGORITHM_TOOL_ENABLED: isAlgorithmToolEnabledForContext(context)
          ? "true"
          : undefined,
        SECURITY_TOOL_ENABLED: isSecurityToolEnabledForContext(context)
          ? "true"
          : undefined,
      },
    };
    const response = flushAuthCookies(request, new Response(null));
    return data(result, { headers: response.headers });
  }
}

/*
loader が 404/500 を投げた route の meta は呼ばれないので、root が
既定の題を出す (#165)。子 route が title を返す通常ページでは、
最深 route の meta がまるごと勝つため既存の <title> は変わらない
*/
export const meta: MetaFunction = ({ error, location }) =>
  generateRootFallbackMeta(error, location.pathname);

export function shouldRevalidate({
  formData,
  defaultShouldRevalidate,
  ...submission
}: ShouldRevalidateFunctionArgs) {
  // The lesson completion action immediately navigates within LuaGate. Avoid
  // refetching global teams/plan data in the short gap before that navigation.
  if (formData?.get("intent") === "completeAndAdvance") return false;
  if (shouldSkipRootRevalidationForLessonFetcher({ ...submission, formData })) {
    return false;
  }
  return defaultShouldRevalidate;
}

function GlobalSearchWithShortcut() {
  const { isOpen, openSearch, closeSearch, windowClaimed } = useGlobalSearch();

  // Enable Cmd+K / Ctrl+K global search
  useCmdK(() => openSearch());

  // /luagate の外枠は自分の窓 (LgSearchWindow) を持つので、そこでは古い窓を開かない
  return <GlobalSearch isOpen={isOpen && !windowClaimed} onClose={closeSearch} />;
}

// ログインユーザーを Sentry に紐づける。エラー発生時に「誰が踏んだか」を
// issue / Slack 通知で追えるようにする。ログアウト時は user をクリアする。
function SentryUserSync() {
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      setSentryUser({
        id: user.id,
        email: user.email,
        username: user.name,
      });
    } else {
      setSentryUser(null);
    }
  }, [user]);

  return null;
}

/*
暗い版 (html の data-theme) は /luagate などだけのもの。公開の頁へ移ったら、残っていれば外す。
最初の描画では動かさない (頁自身が付けた値を消さないため)
*/
function HtmlThemeGuard({ pathname }: { pathname: string }) {
  const previous = useRef(pathname);
  useEffect(() => {
    if (previous.current === pathname) return;
    previous.current = pathname;
    releaseHtmlThemeOutsideOwners(pathname, document.documentElement);
  }, [pathname]);
  return null;
}

/*
<html lang> と dir を、住所の :lang (/en/visualize など) に合わせる。

**params.lang ではなく loader が返した値 (matches[].data.lang) を見る。**
params は住所の文字列をそのまま持つので、404 を返した頁でも :lang の値を
持っている (/ar/visualize/存在しない物 のように、loader が 404 を投げても
params.lang は "ar" のまま)。それだと 404 の画面まで lang="ar" dir="rtl" に
なってしまう。loader が「この頁はこの言語で出す」と決めて返した値だけを見れば、
404 (loader が投げて、その route の data が無い) は何も拾えず既定の ja に
戻る。日本語 (接頭辞なし) と、まだ lang を返さない頁も "ja"/"ltr" のまま
*/
function useDocumentLocale() {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i--) {
    const lang = (matches[i].data as { lang?: unknown } | undefined)?.lang;
    if (typeof lang === "string" && isLocaleCode(lang)) return LOCALES[lang];
  }
  return LOCALES.ja;
}

export function Layout({ children }: { children: React.ReactNode }) {
  const { pathname, search } = useLocation();
  const documentLocale = useDocumentLocale();
  // 親のページが計測している埋め込み (/skillmap/demo?embed=1) では GTM を読まない
  const muteAnalytics = IS_STAGING_BUILD || isAnalyticsMutedEmbed(pathname, search);
  const isLessonVideoEmbed =
    pathname === "/embed/lesson-video" ||
    pathname.startsWith("/embed/lesson-video/");
  // Try to get loader data for teams, planInfo and ENV - may not be available in error boundary
  let teams: Team[] = [];
  let planInfo: PlanInfo | null = null;
  let luagateEntry = false;
  let luagateRole: string | null = null;
  let teamsKnown = false;
  let authBootstrap: AuthBootstrapStatus = "unknown";
  let ENV: RootLoaderData["ENV"] = {};
  try {
    const data = useLoaderData<RootLoaderData>();
    teams = data?.teams ?? [];
    planInfo = data?.planInfo ?? null;
    luagateEntry = data?.luagateEntry ?? false;
    luagateRole = data?.luagateRole ?? null;
    teamsKnown = data?.teamsKnown ?? false;
    authBootstrap = data?.authBootstrap ?? "unknown";
    ENV = data?.ENV ?? {};
  } catch {
    // Loader data not available (e.g., in error boundary)
  }

  // Browser-only and one-shot. This is set before the nested Better Auth UI
  // provider mounts so its first /get-session read can share the SSR proof.
  primeAuthClientBootstrap(authBootstrap);

  // 子の effect より後に走る。エラー画面がハイドレーション中かどうかをここで分ける
  useEffect(() => {
    markDocumentHydrated();
  }, []);

  return (
    <html lang={documentLocale.htmlLang} dir={documentLocale.dir}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* ファビコン。SVG に対応したブラウザはそちらを使い、
            対応していないものは /favicon.ico に落ちる */}
        <link rel="icon" href="/favicon.svg" type="image/svg+xml" />
        <link
          rel="alternate icon"
          href="/favicon.ico"
          sizes="16x16 32x32 48x48"
        />
        {/* PWA */}
        <link rel="manifest" href="/manifest.json" />
        <meta name="theme-color" content="#05B45B" />
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta
          name="apple-mobile-web-app-status-bar-style"
          content="black-translucent"
        />
        <meta name="apple-mobile-web-app-title" content="ゆめさく" />
        {!slideQaLocalOnly && (
          <>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link
              rel="preconnect"
              href="https://fonts.gstatic.com"
              crossOrigin="anonymous"
            />
            {/* print のままだと優先度が最低になる。preload で早く取りにいく */}
            <link rel="preload" as="style" href={NOTO_SANS_JP_STYLESHEET} />
            {/* 下の script が media を書き換えるので、属性の食い違いを警告しない */}
            <link
              id={NOTO_SANS_JP_STYLESHEET_ID}
              rel="stylesheet"
              href={NOTO_SANS_JP_STYLESHEET}
              media="print"
              suppressHydrationWarning
            />
            <script
              dangerouslySetInnerHTML={{ __html: NOTO_SANS_JP_ACTIVATE_SCRIPT }}
            />
            <noscript>
              <link rel="stylesheet" href={NOTO_SANS_JP_STYLESHEET} />
            </noscript>
          </>
        )}
        <Meta />
        <Links />
      </head>
      <body className="bg-white">
        <HtmlThemeGuard pathname={pathname} />
        {/* Inject environment variables for client-side use */}
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(ENV)};`,
          }}
        />
        {IS_STAGING_BUILD && !isDemoPath(pathname) && (
          <div
            role="status"
            // 下の真ん中の小さな札。左下のサイドバーの「畳む」と、画面いっぱいの頁 (クラウドエディターなど) の上の帯に重ならない。押せる物の邪魔をしないよう pointer-events を切る
            style={{ position: "fixed", left: "50%", transform: "translateX(-50%)", bottom: 0, zIndex: 2147483000, pointerEvents: "none", background: "#14231A", color: "#ffffff", fontSize: 11, lineHeight: "16px", height: 16, padding: "0 8px", fontWeight: 700, borderTopLeftRadius: 6, borderTopRightRadius: 6, whiteSpace: "nowrap" }}
          >
            STAGING 本番のデータに書かれます
          </div>
        )}
        {!slideQaLocalOnly && !muteAnalytics && (
          <noscript>
            <iframe
              src="https://www.googletagmanager.com/ns.html?id=GTM-N6D6TQT2"
              height="0"
              width="0"
              style={{ display: "none", visibility: "hidden" }}
            />
          </noscript>
        )}
        <MotionUIThemeProvider theme={motionTheme}>
          <I18nProvider>
            <AuthProvider bootstrapStatus={authBootstrap}>
              <SentryUserSync />
              <ReferralLandingSync />
              <AnalyticsIdentitySync />
              <TeamProvider
                initialTeams={teams}
                initialPlanInfo={planInfo}
                luagateEntry={luagateEntry}
                luagateRole={luagateRole}
                teamsKnown={teamsKnown}
              >
                <PostHogProvider>
                  <ReferralShareProvider>
                    <BreadcrumbProvider>
                      <HeaderProvider>
                        <ToastProvider>
                          <GlobalSearchProvider>
                            {children}
                            {/* Global Search Modal (Cmd+K / Ctrl+K) */}
                            {!isLessonVideoEmbed && <GlobalSearchWithShortcut />}
                          </GlobalSearchProvider>
                        </ToastProvider>
                      </HeaderProvider>
                    </BreadcrumbProvider>
                  </ReferralShareProvider>
                </PostHogProvider>
              </TeamProvider>
            </AuthProvider>
          </I18nProvider>
        </MotionUIThemeProvider>
        <ScrollRestoration />
        <Scripts />
        {!slideQaLocalOnly && (
          <>
            <script
              dangerouslySetInnerHTML={{
                __html: `if('serviceWorker' in navigator){navigator.serviceWorker.register('/sw.js')}`,
              }}
            />
            {/* Google Tag Manager - queue now, fetch after load + idle */}
            {!muteAnalytics && (
              <script
                dangerouslySetInnerHTML={{
                  __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':Date.now(),event:'gtm.js'});var started=false;function load(){if(started)return;started=true;var j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;d.head.appendChild(j)}function schedule(){if(typeof w.requestIdleCallback==='function'){w.requestIdleCallback(load,{timeout:5000})}else{w.setTimeout(load,3000)}}if(d.readyState==='complete'){schedule()}else{w.addEventListener('load',schedule,{once:true})}})(window,document,'script','dataLayer','GTM-N6D6TQT2')`,
                }}
              />
            )}
          </>
        )}
      </body>
    </html>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  const rootData = useRouteLoaderData("root") as
    | Pick<RootLoaderData, "errorRef">
    | undefined;
  const serverErrorRef = rootData?.errorRef;
  const errorRef = useMemo(
    () => pickErrorRef(serverErrorRef),
    // 同じエラーの間は番号を変えない
    [error],
  );

  // Sentry に明示的に送信 (4xx 系は通常スキップ、500 / 予期しない例外のみ記録)
  useEffect(() => {
    const tags = { error_ref: errorRef ?? "none" };
    if (isRouteErrorResponse(error)) {
      if (error.status >= 500) {
        captureSentryException(error, tags);
      }
    } else if (error) {
      captureSentryException(error, tags);
    }
  }, [error, errorRef]);

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return <NotFoundScreen error={error} />;
    }

    /*
    404 以外は、返しているコードをそのまま画面に出す。

    500 だけを特別扱いしていたので、503 も 502 も画面には「500」と
    出ていた。フェイルクローズで 503 を返した日に、curl と画面で数字が
    食い違って切り分けを誤らせた (2026-09-03)
    */
    return <ServerErrorScreen status={error.status} errorRef={errorRef} />;
  }

  // 素の例外にはステータスが無い。500 として扱う
  return <ServerErrorScreen status={500} errorRef={errorRef} />;
}

export default function App() {
  /*
  幕はここで掛ける。**ルートより外に無いと context が届かない。**
  最初はレッスンのページの中に置いていたが、useLessonNavigation を
  呼んでいるのは同じ component の中で、Provider より上にある。
  useContext は null を返し、幕は一度も出なかった
  */
  const { footerSections } = useLoaderData<RootLoaderData>();
  return (
    <PageFooterSectionsProvider value={footerSections ?? []}>
      <LessonGlossaryTermsProvider value={renderLessonGlossaryTerms}>
        <LessonCurtainProvider>
          <Outlet />
        </LessonCurtainProvider>
      </LessonGlossaryTermsProvider>
    </PageFooterSectionsProvider>
  );
}
